import { Box, Card, Checkbox, IconButton, Modal, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { green, grey, yellow } from '@mui/material/colors';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { CardMedium, TableHeader } from '../../../../../app/AppStyles';
import { GetDate, GetTime } from '../../../../../app/AppUtils';
import ContentLoader from '../../../../subcomponents/ContentLoader';
import { fetchSearchStudent, fetchSearchStudentAttendances, putAbsenceBlock, resetAttendanceStatus, setFailedAttendance, setSelectedAttendance } from '../../searchStudentSlice';
import SearchStudentAttendanceEditCard from './SearchStudentAttendanceEditCard';
import ConfirmationModal from '../../../../subcomponents/ConfirmationModal';

import CircleIcon from '@mui/icons-material/Circle';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';

export default function SearchStudentAttendanceTab({ studentId }) {
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.user);
    const { searchStudentAttendance, searchStudentAttendanceStatus, putAbsenceBlockStatus } = useSelector((state) => state.search.student);
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { appSettings } = useSelector((state) => state.settings.app);
    const { activeDepartment } = useSelector((state) => state.settings.theme);
    const { siomSettings } = useSelector((state) => state.settings.siom);
    const { aspNetUser } = useSelector((state) => state.settings.app);

    const [openEdit, setOpenEdit] = useState(false);
    const [departmentId, setDepartmentId] = useState(null);

    // Put status callback
    useEffect(() => {
        if (putAbsenceBlockStatus === "success") {
            dispatch(resetAttendanceStatus());
            dispatch(fetchSearchStudentAttendances({ studentId: studentId, departmentId: departmentId }));

            if (!siomSettings.useMultipleDepartments) {
                dispatch(fetchSearchStudent({ studentId: studentId, employeeId: user.userId, departmentId: null }));
            }

            if (siomSettings.useMultipleDepartments && activeDepartment) {
                dispatch(fetchSearchStudent({ studentId: studentId, employeeId: user.userId, departmentId: departmentId }));
            }
        } else if (putAbsenceBlockStatus === "failed") {
            dispatch(resetAttendanceStatus());
        };
    }, [dispatch, putAbsenceBlockStatus]);

    const editClick = (attendance) => {
        dispatch(setSelectedAttendance(attendance));
        setOpenEdit(true);
    };

    const absenceBlockClick = (id) => {
        dispatch(putAbsenceBlock(id));
    };

    const callbackModalEdit = () => {
        setOpenEdit(false);
        dispatch(resetAttendanceStatus());
        dispatch(setSelectedAttendance(null));
        dispatch(setFailedAttendance(false));
    };

    useEffect(() => {
        setDepartmentId(siomSettings.useMultipleDepartments && activeDepartment ? activeDepartment[0]?.id : null);
    }, [siomSettings.useMultipleDepartments, activeDepartment]);

    useEffect(() => {
        dispatch(fetchSearchStudentAttendances({ studentId: studentId, departmentId: siomSettings.useMultipleDepartments && activeDepartment ? activeDepartment[0]?.id : null }));
    }, []);

    return (
        <>
            <Card elevation={0} sx={{ ...CardMedium, display: 'flex' }}>
                <ContentLoader
                    successCondition={searchStudentAttendanceStatus === 'success'}
                    errorCondition={searchStudentAttendanceStatus === 'failed'}
                    customError={
                        <Card elevation={0} sx={{ ...CardMedium }}>
                            <Typography variant="body1" textAlign="center">{resourceFinal[appLanguage].messages.SEARCH_STUDENT_NO_CONTENT}</Typography>
                            <Typography variant="body1" textAlign="center">{resourceFinal[appLanguage].messages.SEARCH_STUDENT_NO_CONTENT_REASON}</Typography>
                        </Card>
                    }
                    content={
                        <Box sx={{ width: 1 }}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.ACTIVITY}</TableCell>
                                            <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.MOMENT} {resourceFinal[appLanguage].words.DATE} </TableCell>
                                            <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.DISPENSATION}</TableCell>
                                            <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.PRESENCE}</TableCell>
                                            <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.REMARK}</TableCell>
                                            {siomSettings.enableAbsenceBlock && <TableCell align="center" sx={{ ...TableHeader }} padding="checkbox">{resourceFinal[appLanguage].messages.ABSENCE_BLOCK_HEADER}</TableCell>}
                                            {appSettings.appVersion === 2 && <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.HOURS}</TableCell>}
                                            {aspNetUser?.role?.includes("Attendances") && <TableCell align="center" padding="checkbox"></TableCell>}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {searchStudentAttendance?.map((item, index) =>
                                            <TableRow key={index}>
                                                <TableCell align="left">{item.activityName}</TableCell>
                                                <TableCell align="center">{`${GetDate(item.momentDate)} ${GetTime(item.momentDate)}`}</TableCell>
                                                <TableCell align="center"><Checkbox disabled checked={!!item.dispensation} /></TableCell>
                                                <TableCell align="center">
                                                    <CircleIcon sx={{
                                                        color: item.present == null ? grey[500] : (item.dispensation ? green[500] : item.present ? green[500] : yellow[700])
                                                    }} />
                                                </TableCell>
                                                <TableCell align="center">{item.remark}</TableCell>
                                                {siomSettings.enableAbsenceBlock &&
                                                    <TableCell align="center">
                                                        <ConfirmationModal
                                                            iconButton
                                                            buttonColor="secondary"
                                                            icon={<CheckIcon />}
                                                            message={`${resourceFinal[appLanguage].messages.ABSENCE_BLOCK_CONFIRM}`}
                                                            confirmAction={() => absenceBlockClick(item.id)}
                                                            disabledCondition={!item.blockEnabled}
                                                        />
                                                    </TableCell>
                                                }
                                                {appSettings.appVersion === 2 && <TableCell align="center">{item.workedHours}</TableCell>}
                                                {aspNetUser?.role?.includes("Attendances") &&
                                                    <TableCell align="center">
                                                        <IconButton onClick={() => editClick(item)}>
                                                            <EditIcon color="secondary" />
                                                        </IconButton>
                                                    </TableCell>
                                                }
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    }
                />
            </Card>

            <Modal open={openEdit} onClose={callbackModalEdit}>
                <>
                    <SearchStudentAttendanceEditCard handleClose={callbackModalEdit} studentId={studentId} departmentId={departmentId} />
                </>
            </Modal>
        </>
    );
}
